/**
 * productFinderTone — the light/dark-band color decisions for
 * ProductFinderSection, in one pure map.
 *
 * The page dispatcher paints the section band and passes the authoritative
 * `onDark` flag; the quiz must flip its foreground colors on a dark band or
 * the fixed light-page tokens (`--color-text-primary`, `--color-primary`)
 * render dark-on-dark (~1.3:1 on a navy `surface_source: 'primary'` band —
 * design-audit FINDING-001, 2026-07-22). Pure + standalone (no react) so the
 * flip is unit-tested directly, mirroring productFinderPayoff.ts.
 *
 * On-dark choices mirror the established band patterns:
 *   - text: `--color-on-surface-inverse` at full / 75% strength
 *     (SectionHeader / MediaChecklistSection)
 *   - option-button hover: white fill + `--cta-on-dark-primary-fg` label
 *     (DARK_BAND_CTA_VARS in styleOverrides.ts)
 */

// Deliberately unexported (knip flags exported types with no importer); the
// shape is exposed structurally via productFinderTone's return type.
type ProductFinderTone = {
  /** Inline-style color for headings + full-contrast text. */
  heading: string
  /** Inline-style color for secondary/muted copy. */
  muted: string
  /** Inline-style color for text links ("Start over", runner-up, explore). */
  link: string
  /** Progress-bar track background class. */
  track: string
  /** Progress-bar fill class. */
  fill: string
  /** Quiz option-button border/background/text + hover classes. */
  option: string
  /** Back-button text + hover classes. */
  back: string
  /** Hairline divider border-color class (runner-up row). */
  divider: string
}

export function productFinderTone(onDark: boolean): ProductFinderTone {
  if (onDark) {
    return {
      heading: 'var(--color-on-surface-inverse, #f8fafc)',
      muted: 'var(--section-on-surface-muted, var(--color-on-surface-inverse-muted))',
      link: 'var(--color-on-surface-inverse, #f8fafc)',
      track: 'bg-white/20',
      fill: 'bg-white',
      option:
        'border-white/25 bg-white/10 text-[var(--color-on-surface-inverse,#f8fafc)] hover:border-white hover:bg-white hover:text-[var(--cta-on-dark-primary-fg,var(--brand-primary,#0f172a))]',
      back: 'text-[var(--color-on-surface-inverse-muted)] hover:text-[var(--color-on-surface-inverse,#f8fafc)]',
      divider: 'border-white/25',
    }
  }
  return {
    heading: 'var(--color-text-primary)',
    muted: 'var(--color-text-secondary)',
    link: 'var(--color-primary)',
    track: 'bg-[var(--color-surface-muted)]',
    fill: 'bg-[var(--color-primary)]',
    option:
      // Hover fill is --color-primary, so the label must be the token
      // contrast-picked FOR that fill (--color-primary-text). NOT
      // --color-cta-text: that one is picked for the CTA fill, and on a
      // light-accent brand (AE: orange CTA → dark label) it renders
      // dark-on-dark against a dark primary hover fill.
      'border-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-text-primary)] hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-[var(--color-primary-text,#fff)]',
    back: 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
    divider: 'border-[var(--color-border)]',
  }
}
